import { useMemo } from "react";
import { Clock, Loader2, Trash2, Workflow } from "lucide-react";
import { toast } from "sonner";
import { useFluxos } from "@/hooks/useFluxos";
import { useTemplates } from "@/hooks/useTemplates";
import {
  useScheduledMessages,
  type ScheduledMessage,
} from "@/hooks/useScheduledMessages";

function formatScheduledAt(iso: string): string {
  try {
    return new Intl.DateTimeFormat("pt-BR", {
      dateStyle: "short",
      timeStyle: "short",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

interface LeadScheduledMessagesProps {
  contactId: string;
}

export function LeadScheduledMessages({ contactId }: LeadScheduledMessagesProps) {
  const { templates } = useTemplates();
  const { fluxos } = useFluxos();
  const {
    scheduledMessages,
    loading,
    cancelScheduledMessage,
    deleteScheduledMessage,
  } = useScheduledMessages(contactId);

  const pending = useMemo(
    () =>
      scheduledMessages.filter(
        (m) => m.status === "pending" || m.status === "processing"
      ),
    [scheduledMessages]
  );

  const itemLabel = (msg: ScheduledMessage): string => {
    if ((msg.kind ?? "template") === "fluxo") {
      return fluxos.find((f) => f.id === msg.fluxo_id)?.name ?? "Fluxo";
    }
    return (
      templates.find((t) => t.id === msg.template_id)?.name ??
      (msg.content_snapshot?.trim().slice(0, 40) || "Resposta rápida")
    );
  };

  const handleCancel = async (msg: ScheduledMessage) => {
    if (msg.status !== "pending") return;

    const cancelled = await cancelScheduledMessage(msg.id);
    if (!cancelled.ok) {
      toast.error(cancelled.error ?? "Não foi possível cancelar o agendamento.");
      return;
    }

    const removed = await deleteScheduledMessage(msg.id);
    if (!removed.ok) {
      toast.error(removed.error ?? "Não foi possível excluir o agendamento.");
      return;
    }

    toast.success("Agendamento cancelado e excluído. A mensagem não será enviada.");
  };

  return (
    <div className="flex flex-col gap-3">
      {loading ? (
        <p className="text-xs text-muted-foreground dark:text-neutral-500 flex items-center gap-2">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          Carregando…
        </p>
      ) : pending.length === 0 ? (
        <p className="text-xs text-muted-foreground dark:text-neutral-500">
          Nenhum agendamento pendente.
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {pending.map((msg) => {
            const isFluxo = (msg.kind ?? "template") === "fluxo";
            const canCancel = msg.status === "pending";
            return (
              <li
                key={msg.id}
                className="rounded-lg border border-border dark:border-neutral-700/50 bg-card-muted/40 dark:bg-neutral-800/40 p-2.5 flex flex-col gap-1.5"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1 flex flex-col gap-1">
                    <div className="flex items-center gap-1.5 min-w-0">
                      {isFluxo && (
                        <Workflow className="w-3.5 h-3.5 text-primary dark:text-blue-400 shrink-0" />
                      )}
                      <span className="text-sm font-medium text-foreground dark:text-neutral-100 truncate">
                        {itemLabel(msg)}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 text-[11px] text-muted-foreground dark:text-neutral-400">
                      <Clock className="w-3 h-3 shrink-0" />
                      <span>{formatScheduledAt(msg.scheduled_at)}</span>
                      {msg.status === "processing" && (
                        <span className="text-primary dark:text-blue-400">· Enviando…</span>
                      )}
                    </div>
                    <p className="text-[11px] text-muted-foreground/80 dark:text-neutral-500">
                      {msg.cancel_on_reply !== false
                        ? "Cancela se o contato responder"
                        : "Não cancela se o contato responder"}
                    </p>
                  </div>
                  {canCancel && (
                    <button
                      type="button"
                      onClick={() => handleCancel(msg)}
                      className="shrink-0 p-1 rounded-md text-muted-foreground hover:text-red-500 hover:bg-red-50 dark:text-neutral-400 dark:hover:text-red-400 dark:hover:bg-neutral-700/50 transition-colors"
                      aria-label="Excluir agendamento"
                      title="Excluir agendamento"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
